import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, ApiClientError } from '@client/lib/api';
import { FieldError } from '@client/components/FieldError';
import { customerLabel } from '@shared/customerLabel';
import { formatPhone } from '@shared/phone';
import { validate, type FieldErrors } from '@client/lib/formErrors';
import { createCustomerSchema } from '@shared/schemas/customer';
import styles from './CustomersPage.module.css';

/** A customer row as returned by GET /api/v1/customers. */
interface CustomerProperty {
  id: string;
  name: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
}

interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  phone: string | null;
  email: string | null;
  isActive: boolean;
  /** Active properties, server-joined so the list can show where the work is. */
  properties?: CustomerProperty[];
}

/**
 * One-line address, empty parts dropped — the same composition the job detail
 * and Today screens use for a property row.
 */
function formatCustomerAddress(p: CustomerProperty): string {
  return [p.addressLine1, p.addressLine2, p.city, p.state, p.postalCode].filter(Boolean).join(', ');
}

/** The server response after creating a customer: the full new row. */
interface CreatedCustomer {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  phone: string | null;
  email: string | null;
  isActive: boolean;
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; customers: Customer[] };

/**
 * The Customers list view.
 *
 * Shows every active customer in the organization. Each row is tappable and
 * navigates to /customers/:id. A "New customer" action opens an inline form
 * that POSTs to /api/v1/customers; on success the list re-renders from the
 * server's response.
 *
 * No optimistic updates: the list only changes once the server confirms.
 */
export function CustomersPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  /** id of the customer currently being created, or null. */
  const [creating, setCreating] = useState(false);
  /** error message for a failed create, or null. */
  const [createError, setCreateError] = useState<string | null>(null);
  /** per-input validation messages, keyed by field name. */
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  /** whether the new-customer form is open. */
  const [showForm, setShowForm] = useState(false);
  /** draft field values for the new-customer form. */
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    phone: '',
    email: '',
  });

  const fetchCustomers = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const data = await apiGet<Customer[]>('/api/v1/customers');
      setState({ kind: 'loaded', customers: data });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof ApiClientError ? err.message : 'Could not load customers.',
      });
    }
  }, []);

  useEffect(() => {
    void fetchCustomers();
  }, [fetchCustomers]);

  const handleCreate = useCallback(async () => {
    setCreateError(null);

    // Validate with the SERVER'S schema before sending. Not a convenience: the
    // server answers with one message for the whole form, so without this a
    // mistyped phone number and a mistyped email are indistinguishable to
    // whoever has to fix them.
    const result = validate(createCustomerSchema, form);
    if (!result.ok) {
      setFieldErrors(result.fields);
      setCreateError(result.form);
      return;
    }
    setFieldErrors({});

    setCreating(true);
    try {
      // The PARSED value, not the raw form: trimmed, and with blank optional
      // fields normalized away rather than sent as empty strings.
      const created = await apiPost<CreatedCustomer>('/api/v1/customers', result.data);
      // Re-render from the SERVER's response: prepend the confirmed row.
      setState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        return { kind: 'loaded', customers: [created, ...prev.customers] };
      });
      setShowForm(false);
      setForm({ firstName: '', lastName: '', companyName: '', phone: '', email: '' });
    } catch (err) {
      // On failure: keep the form open, leave the list unchanged, surface the
      // server's message.
      setCreateError(
        err instanceof ApiClientError
          ? err.message
          : 'Could not create this customer. Please try again.'
      );
    } finally {
      setCreating(false);
    }
  }, [form]);

  const handleFieldChange =
    (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      // Clear this field's error as soon as it is edited. Leaving it up while
      // someone fixes the value makes a corrected field look still-broken, and
      // it will be re-validated on submit anyway.
      setFieldErrors((prev) => {
        if (prev[field] === undefined) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    };

  /**
   * Rewrite a phone number into the canonical format when the field is left.
   *
   * ON BLUR, not on every keystroke. Reformatting mid-typing fights the person
   * doing it: inserting `(` and `) ` moves the caret out from under them, and
   * deleting a character they did not type is worse than showing an unformatted
   * number for a few seconds. Leaving the field is the moment they are done.
   *
   * Only rewrites a number that is actually valid. An incomplete one is left
   * exactly as typed so the validation message can explain it, rather than
   * being mangled into something that looks deliberate.
   */
  const handlePhoneBlur = () => {
    setForm((prev) => {
      const formatted = formatPhone(prev.phone);
      if (!formatted || formatted === prev.phone) return prev;
      return { ...prev, phone: formatted };
    });
  };

  const isFormEmpty =
    !form.firstName && !form.lastName && !form.companyName && !form.phone && !form.email;

  if (state.kind === 'loading') {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} aria-live="polite">
          Loading customers…
        </p>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} role="alert">
          {state.message}
        </p>
        <button type="button" className={styles.retryButton} onClick={() => void fetchCustomers()}>
          Retry
        </button>
      </main>
    );
  }

  const { customers } = state;

  return (
    <main id="main" className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Customers</h1>
        <button type="button" className={styles.newButton} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'New customer'}
        </button>
      </header>

      {showForm && (
        <section className={styles.formSection} aria-label="New customer">
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="firstName">
              First name
            </label>
            <input
              id="firstName"
              type="text"
              className={styles.input}
              value={form.firstName}
              onChange={handleFieldChange('firstName')}
              disabled={creating}
              aria-invalid={fieldErrors.firstName ? 'true' : undefined}
              aria-describedby={fieldErrors.firstName ? 'firstName-error' : undefined}
            />
            <FieldError id="firstName-error" message={fieldErrors.firstName} />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="lastName">
              Last name
            </label>
            <input
              id="lastName"
              type="text"
              className={styles.input}
              value={form.lastName}
              onChange={handleFieldChange('lastName')}
              disabled={creating}
              aria-invalid={fieldErrors.lastName ? 'true' : undefined}
              aria-describedby={fieldErrors.lastName ? 'lastName-error' : undefined}
            />
            <FieldError id="lastName-error" message={fieldErrors.lastName} />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="companyName">
              Company name
            </label>
            <input
              id="companyName"
              type="text"
              className={styles.input}
              value={form.companyName}
              onChange={handleFieldChange('companyName')}
              disabled={creating}
              aria-invalid={fieldErrors.companyName ? 'true' : undefined}
              aria-describedby={fieldErrors.companyName ? 'companyName-error' : undefined}
            />
            <FieldError id="companyName-error" message={fieldErrors.companyName} />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="phone">
              Phone
            </label>
            <input
              id="phone"
              type="tel"
              className={styles.input}
              value={form.phone}
              onChange={handleFieldChange('phone')}
              onBlur={handlePhoneBlur}
              disabled={creating}
              aria-invalid={fieldErrors.phone ? 'true' : undefined}
              aria-describedby={fieldErrors.phone ? 'phone-error' : undefined}
            />
            <FieldError id="phone-error" message={fieldErrors.phone} />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              className={styles.input}
              value={form.email}
              onChange={handleFieldChange('email')}
              disabled={creating}
              aria-invalid={fieldErrors.email ? 'true' : undefined}
              aria-describedby={fieldErrors.email ? 'email-error' : undefined}
            />
            <FieldError id="email-error" message={fieldErrors.email} />
          </div>
          {createError && (
            <p className={styles.createError} role="alert">
              {createError}
            </p>
          )}
          <button
            type="button"
            className={styles.saveButton}
            disabled={creating || isFormEmpty}
            aria-busy={creating}
            onClick={() => void handleCreate()}
          >
            {creating ? 'Saving…' : 'Save'}
          </button>
        </section>
      )}

      {customers.length === 0 ? (
        <p className={styles.empty}>No customers yet. Add one to get started.</p>
      ) : (
        <ul className={styles.customerList}>
          {customers.map((customer) => (
            <li
              key={customer.id}
              className={styles.customerRow}
              onClick={() => navigate(`/customers/${customer.id}`)}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/customers/${customer.id}`);
                }
              }}
            >
              <div className={styles.customerInfo}>
                <span className={styles.customerName}>{customerLabel(customer)}</span>
                {(() => {
                  // The address IS the point of this screen: which house, and
                  // which of several. Server joins active properties; an
                  // org-scoped inner join means the array is never from
                  // another customer.
                  const props = customer.properties ?? [];
                  const first = props[0];
                  if (!first) return null;
                  const rest = props.slice(1);
                  return (
                    <span
                      className={styles.customerAddress}
                      aria-label={
                        rest.length > 0
                          ? `${formatCustomerAddress(first)} and ${rest.length} more`
                          : undefined
                      }
                    >
                      {formatCustomerAddress(first)}
                      {rest.length > 0 && (
                        <span className={styles.customerAddressMore}> · +{rest.length} more</span>
                      )}
                    </span>
                  );
                })()}
                {customer.phone && <span className={styles.customerPhone}>{customer.phone}</span>}
                {customer.email && <span className={styles.customerEmail}>{customer.email}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
